import { FieldValues } from 'react-hook-form'

import { SearchFilterFilters } from '@api/orval/apiHooks'
import { QueryParam } from '@components/Filters/filter.types'
import { dayjs } from '@lib/dayjs'
import type { IsoDateRange } from '@lib/tz'

type TransformedFilters<T> = {
  [K in keyof T]?: T[K] extends string | number | boolean | undefined ? T[K] : IsoDateRange
}

const ISO_8601_PREFIX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/

function isValidIsoDateString(value: unknown): value is string {
  return typeof value === 'string' && ISO_8601_PREFIX.test(value) && dayjs(value).isValid()
}

export function transformFilters<T extends FieldValues>(filters: SearchFilterFilters): Partial<TransformedFilters<T>> {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (key.endsWith('_from') && isValidIsoDateString(value)) {
        const baseKey = key.slice(0, -5) as keyof T
        if (!acc[baseKey]) acc[baseKey] = { from: undefined, to: undefined } as T[keyof T]
        ;(acc[baseKey] as IsoDateRange).from = value
      } else if (key.endsWith('_to') && isValidIsoDateString(value)) {
        const baseKey = key.slice(0, -3) as keyof T
        if (!acc[baseKey]) acc[baseKey] = { from: undefined, to: undefined } as T[keyof T]
        ;(acc[baseKey] as IsoDateRange).to = value
      } else {
        acc[key as keyof T] = value as T[keyof T]
      }

      return acc
    },
    {} as Partial<TransformedFilters<T>>,
  )
}

export function normalizeAndTransformFilters(filters: QueryParam | undefined) {
  if (!filters) return {}

  const normalizedParams: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(filters)) {
    const normalizedKey = key.endsWith('[]') ? key.slice(0, -2) : key
    normalizedParams[normalizedKey] = value
  }

  const transformed = transformFilters(normalizedParams)

  return transformed
}

const getNormalizedValue = (isArrayKey: boolean, values: string[]) => {
  if (isArrayKey) {
    return values
  }

  return values.length > 1 ? values : values[0]
}

export const paramsDataToFilterValues = (searchParams: URLSearchParams) => {
  const normalizedParams: Record<string, unknown> = {}

  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key)
    const isArrayKey = key.endsWith('[]')
    const normalizedKey = isArrayKey ? key.slice(0, -2) : key

    normalizedParams[normalizedKey] = getNormalizedValue(isArrayKey, values)
  }

  return normalizedParams
}
